// Record Delivery: the fee slices this delivery would post (POST /sales/:id/delivery-events/preview -> fee_slices_due) and, afterwards, the ones it posted
// (fee_slices). Amounts are the server's exact strings, rendered as given: no arithmetic here. A slice of a collect-with-each-delivery fee is confirmed
// here (collected at the till, or left owed). An API without the field (older) gives no slices: the section simply does not render.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { formatMoneyExact, formatQty } from '../../../utils/format';

const nameOf = f => (f?.bucket ? String(f.bucket).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : null);

// fee names by id for the order (best effort: a failed read leaves the generic label)
export function useFeeNames(op, enabled) {
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!op || !enabled) return undefined;
    let live = true;
    api.getSalesFees(op).then(d => { if (live) setNames(Object.fromEntries((d?.sales_fees || []).map(f => [f.id, nameOf(f)]))); }).catch(() => {});
    return () => { live = false; };
  }, [op, enabled]);
  return names;
}

// debounced preview of the delivery event for the payload lines: fee slices, per-line value and totals (E7); empty on any failure or an absent field (silent degrade)
const NONE = { slices: [], lines: [], totals: null };
export function useDeliveryPreview(saleId, lines) {
  const [pv, setPv] = useState(NONE);
  const sig = saleId && lines.length ? JSON.stringify(lines) : '';
  useEffect(() => {
    if (!sig) { setPv(NONE); return undefined; }
    let live = true;
    const h = setTimeout(() => {
      api.previewDeliveryEvent(saleId, { lines: JSON.parse(sig) })
        .then(r => { const d = r?.ok ? r.data : null; if (live) setPv(d ? { slices: Array.isArray(d.fee_slices_due) ? d.fee_slices_due : [], lines: Array.isArray(d.lines) ? d.lines : [], totals: d.totals || null } : NONE); })
        .catch(() => { if (live) setPv(NONE); });
    }, 300);
    return () => { live = false; clearTimeout(h); };
  }, [sig, saleId]);
  return pv;
}

// the fee_collect lines for the body: only the slices that need the delivery step's confirmation (amount echoed as served)
export const feeCollectLines = (slices, collected) => slices.filter(s => s.confirm_required).map(s => ({
  fee_id: s.fee_id, collected: collected[s.fee_id] !== false && s.collected_default !== false, ...(s.account_id ? { account_id: s.account_id } : {}), amount: s.amount }));

export function FeeSlicesDue({ slices, names, collected, onToggle }) {
  const { t } = useTranslation('delivery');
  if (!slices.length) return null;
  return (
    <div data-testid="delivery-fee-slices">
      <div className="sec-lab">{t('fees.due')}</div>
      <div className="gtable-wrap" style={{ overflowX: 'auto' }}><table className="gtable zebra">
        <thead><tr><th>{t('fees.colCollected')}</th><th>{t('fees.colFee')}</th><th className="r">{t('fees.colAmount')}</th></tr></thead>
        <tbody>{slices.map(s => {
          const on = collected[s.fee_id] !== false && s.collected_default !== false;
          return (<tr key={s.fee_id} data-testid={'delivery-fee-' + s.fee_id}>
            <td>{s.confirm_required
              ? <label><input type="checkbox" checked={on} onChange={e => onToggle(s.fee_id, e.target.checked)} data-testid={'delivery-fee-collect-' + s.fee_id} /> {t(on ? 'fees.collected' : 'fees.owed')}</label>
              : <span className="rd-nd">{t('fees.dest.' + (s.destination || 'receivable'), { defaultValue: t('fees.dest.receivable') })}</span>}</td>
            <td>{s.fee_name || names[s.fee_id] || t('fees.generic')}</td>
            <td className="r num">{formatMoneyExact(s.amount)}</td></tr>);
        })}</tbody></table></div>
      <div className="hint">{t('fees.hint')}</div>
    </div>
  );
}

// after the post: what the delivery posted for each fee (response.fee_slices)
export function FeeSlicesPosted({ slices, names }) {
  const { t } = useTranslation('delivery');
  if (!Array.isArray(slices) || !slices.length) return null;
  return (
    <div data-testid="delivery-fee-posted">
      <div className="sec-lab">{t('fees.posted')}</div>
      <div className="gtable-wrap" style={{ overflowX: 'auto' }}><table className="gtable zebra">
        <thead><tr><th>{t('fees.colFee')}</th><th>{t('fees.colWhere')}</th><th className="r">{t('fees.colAmount')}</th></tr></thead>
        <tbody>{slices.map((s, i) => (
          <tr key={(s.finance_id || s.fee_id) + ':' + i}><td>{s.fee_name || names[s.fee_id] || t('fees.generic')}</td>
            <td>{t('fees.dest.' + (s.destination || 'receivable'), { defaultValue: t('fees.dest.receivable') })}</td>
            <td className="r num">{formatMoneyExact(s.amount)}</td></tr>))}</tbody></table></div>
    </div>
  );
}

// E7: the event's value as served (preview before posting, POST answer after): per line value + discount, then goods / discounts / fees / revenue. Server strings only.
export function DeliveryValue({ lines, totals, posted }) {
  const { t } = useTranslation('delivery');
  if (!totals && !(Array.isArray(lines) && lines.length)) return null;
  const rows = Array.isArray(lines) ? lines : [];
  return (
    <div data-testid={posted ? 'delivery-value-posted' : 'delivery-value'}>
      <div className="sec-lab">{t(posted ? 'value.posted' : 'value.due')}</div>
      <div className="gtable-wrap" style={{ overflowX: 'auto' }}><table className="gtable zebra">
        {rows.length > 0 && <><thead><tr><th>{t('value.colItem')}</th><th className="r">{t('value.colQty')}</th><th className="r">{t('value.colPrice')}</th><th className="r">{t('value.colValue')}</th><th className="r">{t('value.colDiscount')}</th></tr></thead>
          <tbody>{rows.map((l, i) => <tr key={(l.line_id || l.sale_id) + ':' + i}><td>{l.item_name || '—'}</td><td className="r num">{formatQty(l.quantity)}</td>
            <td className="r num">{formatMoneyExact(l.unit_price)}</td><td className="r num">{formatMoneyExact(l.value)}</td><td className="r num">{formatMoneyExact(l.discount)}</td></tr>)}</tbody></>}
        {totals && <tfoot>
          <tr><td colSpan={3}>{t('value.goods')}</td><td className="r num" data-testid="dv-goods">{formatMoneyExact(totals.goods)}</td><td /></tr>
          <tr><td colSpan={3}>{t('value.discounts')}</td><td className="r num" data-testid="dv-discounts">{formatMoneyExact(totals.discounts)}</td><td /></tr>
          <tr><td colSpan={3}>{t('value.fees')}</td><td className="r num" data-testid="dv-fees">{formatMoneyExact(totals.fees)}</td><td /></tr>
          <tr><td colSpan={3}><b>{t('value.revenue')}</b></td><td className="r num" data-testid="dv-revenue"><b>{formatMoneyExact(totals.revenue)}</b></td><td /></tr>
        </tfoot>}
      </table></div>
    </div>
  );
}
